import React from 'react';
import {
    Card,
    CardContent,
    CardMedia,
    Box,
    Typography,
    Chip,
    IconButton,
    Checkbox,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    FormControl,
    OutlinedInput,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useCatalogContext } from './CatalogContext';

const CatalogProductCard = ({ produto }) => {
    const {
        selectedProducts,
        handleProductSelection,
        formatPrice,
        editingPriceProductId,
        editingPriceSkuId,
        newPrice,
        setNewPrice,
        handleSavePrice,
        handleCancelEditPrice,
        handleStartEditPrice,
        handleToggleProductActive,
        getProductImageIndex,
        setProductImageIndex
    } = useCatalogContext();

    const [anchorEl, setAnchorEl] = React.useState(null);
    const imgBoxRef = React.useRef();

    // Identificador único do produto
    const productId = produto.id || produto._id;

    // Lista de partnerIds das SKUs do produto
    const skuPartnerIds = Array.isArray(produto.skus) ? produto.skus.map(sku => sku.partnerId) : [];

    // Considera selecionado se todos os SKUs do produto estiverem selecionados
    const isSelected = skuPartnerIds.length > 0 && skuPartnerIds.every(skuId => selectedProducts.includes(skuId));
    const isEditing = editingPriceProductId === produto.id && editingPriceSkuId === produto?.skus[0]?.partnerId;
    const hasImage = produto?.images?.length > 0 && produto.images[0];
    const isActive = produto.isProductActive;
    const stock = produto?.skus?.[0]?.amount || 0;

    // Preço de Mercado e Vitrine
    const precoMercado = produto?.skus?.[0]?.price || 0;
    const precoVitrine = produto?.meli_props?.price || null;
    const meliStatus = produto?.meli_props?.status || '-';
    const meliAvailable = produto?.meli_props?.available_quantity ?? '-';
    const meliPermalink = produto?.meli_props?.permalink || null;
    const meliDateCreated = produto?.meli_props?.date_created ? new Date(produto.meli_props.date_created).toLocaleDateString() : '-';
    const meliSku = produto?.meli_props?.seller_custom_field || produto?.meli_props?.attributes.find((v) => v.id === 'SELLER_SKU')?.value_name || '-';
    const canal = produto?.channel || '-';
    const handleMenuClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleCheckboxClick = (event) => {
        event.stopPropagation();
        // Seleciona ou desseleciona todos os SKUs do produto
        handleProductSelection(event, skuPartnerIds);
    };

    const getStockLabel = () => {
        if (stock === 0) return { label: 'Esgotado', color: '#f44336', bgColor: '#ffebee' };
        if (stock < 10) return { label: 'Baixo', color: '#ff9800', bgColor: '#fff3e0' };
        return { label: 'Disponível', color: '#4caf50', bgColor: '#e8f5e9' };
    };

    const stockInfo = getStockLabel();

    const images = Array.isArray(produto.images) ? produto.images : [];
    const totalImages = images.length;
    const imgIndex = getProductImageIndex(productId);
    const currentImage = totalImages > 0 ? (images[imgIndex]?.url || images[imgIndex]) : 'https://via.placeholder.com/300x300?text=Sem+Imagem';

    // Swipe/touch navigation
    React.useEffect(() => {
        const box = imgBoxRef.current;
        if (!box) return;
        let startX = null;
        let moved = false;
        const onTouchStart = (e) => {
            startX = e.touches[0].clientX;
            moved = false;
        };
        const onTouchMove = (e) => {
            if (startX === null) return;
            const dx = e.touches[0].clientX - startX;
            if (Math.abs(dx) > 30 && !moved) {
                moved = true;
                if (dx > 0) handlePrevImage(e);
                else handleNextImage(e);
            }
        };
        const onTouchEnd = () => { startX = null; moved = false; };
        box.addEventListener('touchstart', onTouchStart);
        box.addEventListener('touchmove', onTouchMove);
        box.addEventListener('touchend', onTouchEnd);
        return () => {
            box.removeEventListener('touchstart', onTouchStart);
            box.removeEventListener('touchmove', onTouchMove);
            box.removeEventListener('touchend', onTouchEnd);
        };
    }, [imgBoxRef, totalImages, productId]);

    // Atualiza o índice do carrossel no contexto
    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (totalImages === 0) return;
        setProductImageIndex(productId, (imgIndex - 1 + totalImages) % totalImages);
    };
    const handleNextImage = (e) => {
        e.stopPropagation();
        if (totalImages === 0) return;
        setProductImageIndex(productId, (imgIndex + 1) % totalImages);
    };

    return (
        <Card
            elevation={0}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: 2,
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: '1px solid #e0e0e0',
                boxShadow: isSelected ? '0 0 0 2px #90caf9' : '0 2px 8px rgba(0,0,0,0.05)',
                '&:hover': {
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)'
                },
                bgcolor: isActive ? '#fff' : '#f5f5f5',
            }}
        >
            {/* Checkbox para seleção */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 1
                }}
            >
                <Checkbox
                    checked={isSelected}
                    onChange={handleCheckboxClick}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        borderRadius: '50%',
                        padding: 0.5,
                        '&.Mui-checked': {
                            color: '#1976d2'
                        }
                    }}
                />
            </Box>

            {/* Menu de opções */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1
                }}
            >
                <IconButton
                    onClick={handleMenuClick}
                    size="small"
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)'
                        }
                    }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    elevation={2}
                    PaperProps={{
                        sx: { minWidth: 180, borderRadius: 2 }
                    }}
                >
                    <MenuItem disabled>
                        <ListItemIcon>
                            <EditOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Editar Anúncio" />
                    </MenuItem>
                    <MenuItem onClick={() => {
                        // Ação para editar CMV
                        // Pode abrir um diálogo ou chamar função do contexto
                        handleStartEditPrice(produto);
                        handleMenuClose();
                    }}>
                        <ListItemIcon>
                            <LocalOfferIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Editar CMV" />
                    </MenuItem>
                    <MenuItem disabled>
                        <ListItemIcon>
                            <ToggleOffIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Desativar" />
                    </MenuItem>
                </Menu>
            </Box>

            {/* Status badge */}
            {!isActive && (
                <Chip
                    label="Inativo"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 48,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        fontSize: '0.75rem'
                    }}
                />
            )}

            {/* Imagem do produto com carrossel melhorado */}
            <Box sx={{ position: 'relative', cursor: totalImages > 1 ? 'grab' : 'default' }} ref={imgBoxRef}
                onMouseDown={e => e.preventDefault()} // previne arrasto default
            >
                <CardMedia
                    component="img"
                    image={currentImage}
                    alt={produto.title}
                    sx={{
                        height: 200,
                        objectFit: 'contain',
                        backgroundColor: '#f5f5f5',
                        opacity: isActive ? 1 : 0.6,
                        userSelect: 'none',
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s'
                    }}
                />
                {totalImages > 1 && (
                    <>
                        {/* Setas só aparecem no hover ou touch */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '&:hover .carousel-arrow': { opacity: 1 }
                        }}>
                            <IconButton
                                className="carousel-arrow"
                                size="small"
                                onClick={handlePrevImage}
                                sx={{
                                    opacity: 0,
                                    ml: 0.5,
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    zIndex: 2,
                                    transition: 'opacity 0.2s',
                                    pointerEvents: 'auto'
                                }}
                            >
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                className="carousel-arrow"
                                size="small"
                                onClick={handleNextImage}
                                sx={{
                                    opacity: 0,
                                    mr: 0.5,
                                    bgcolor: 'rgba(255,255,255,0.7)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    zIndex: 2,
                                    transition: 'opacity 0.2s',
                                    pointerEvents: 'auto'
                                }}
                            >
                                <ArrowForwardIosIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        {/* Dots de navegação */}
                        <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center', zIndex: 2, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            {images.map((img, idx) => (
                                <Box
                                    key={idx}
                                    onClick={e => { e.stopPropagation(); setProductImageIndex(productId, idx); }}
                                    sx={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        bgcolor: idx === imgIndex ? '#1976d2' : 'rgba(255,255,255,0.7)',
                                        border: idx === imgIndex ? '2px solid #1976d2' : '1px solid #bbb',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                />
                            ))}
                        </Box>
                    </>
                )}
            </Box>

            <CardContent sx={{ flexGrow: 1, pt: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }}>
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                    noWrap
                    sx={{ opacity: isActive ? 1 : 0.7, fontSize: '1.08rem', mb: 0.5, textAlign: 'left', lineHeight: 1.2 }}
                >
                    {produto.title}
                </Typography>
                {/* Tags rolantes (scroll horizontal) */}
                <Box sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    gap: 1,
                    pb: 0.5,
                    mb: 0.5,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#e0e0e0', borderRadius: 2 }
                }}>
                    <Chip label={canal} size="small" sx={{ bgcolor: '#fff8e1', color: '#ff8f00', fontWeight: 600, fontSize: '0.7rem' }} />
                    <Chip label={`SKU: ${meliSku}`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }} />
                    {produto.brand?.name && (
                        <Chip label={produto.brand.name} size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {produto.category?.name && (
                        <Chip label={produto.category.name} size="small" sx={{ bgcolor: '#e0f2f1', color: '#00897b', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {produto.origin?.description && (
                        <Chip label={`Origem: ${produto.origin.description}`} size="small" sx={{ bgcolor: '#fffde7', color: '#fbc02d', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {produto.meli_props?.listing_type_id && (
                        <Chip label={`Tipo: ${produto.meli_props.listing_type_id}`} size="small" sx={{ bgcolor: '#e1f5fe', color: '#0288d1', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {produto.meli_props?.condition && (
                        <Chip label={`Condição: ${produto.meli_props.condition === 'new' ? 'Novo' : produto.meli_props.condition}`}
                            size="small" sx={{ bgcolor: '#fce4ec', color: '#c2185b', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                    {produto.characteristics && produto.characteristics.slice(0, 3).map((c) => (
                        <Chip key={c._id} label={`${c.name}: ${c.value}`} size="small" sx={{ bgcolor: '#f5f5f5', color: '#616161', fontWeight: 500, fontSize: '0.7rem' }} />
                    ))}
                </Box>
                {/* Botão Ver anúncio destacado, fixo no topo direito abaixo das tags */}
                {meliPermalink && (
                    <Box sx={{ position: 'absolute', top: 95, right: 5, zIndex: 2 }}>
                        <a href={meliPermalink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <Chip
                                label="Ver anúncio"
                                color="primary"
                                clickable
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.78rem',
                                    bgcolor: '#1976d2',
                                    color: '#fff',
                                    boxShadow: '0 1px 4px rgba(25, 118, 210, 0.10)',
                                    '&:hover': { bgcolor: '#115293' },
                                    mb: 1,
                                    opacity: 0.92
                                }}
                            />
                        </a>
                    </Box>
                )}
                {/* Infos principais e preços: dois boxes lado a lado, sem destaque especial nos preços */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', mb: 1 }}>
                    {/* Coluna 1: Infos de gestão */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                            ID: <Tooltip title={produto?.skus?.[0]?.partnerId || '-'} arrow>
                                <span style={{
                                    color: '#1976d2',
                                    maxWidth: 90,
                                    display: 'inline-block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    verticalAlign: 'bottom',
                                    whiteSpace: 'nowrap',
                                    fontFamily: 'monospace',
                                    fontSize: 'inherit',
                                }}>
                                    {produto?.skus?.[0]?.partnerId && produto.skus[0].partnerId.length > 10
                                        ? produto.skus[0].partnerId.slice(0, 10) + '..'
                                        : produto.skus?.[0]?.partnerId || '-'}
                                </span>
                            </Tooltip>
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={stockInfo.label}
                                variant="outlined"
                                size="small"
                                sx={{ fontWeight: 500, fontSize: '0.7rem', color: stockInfo.color, bgcolor: stockInfo.bgColor, border: 'none' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {/* {stock} unid. / Meli: {meliAvailable} */}
                                {stock} unid.
                            </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                            Status Meli: <span style={{ color: meliStatus === 'active' ? '#388e3c' : '#f44336', fontWeight: 700 }}>{meliStatus}</span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Criado: {meliDateCreated}
                        </Typography>
                        {produto.meli_props?.sold_quantity !== undefined && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Vendidos: <b>{produto.meli_props.sold_quantity}</b>
                            </Typography>
                        )}
                    </Box>
                    {/* Coluna 2: Preços, alinhados como infos normais */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.3, alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                            CMV: <span style={{ color: '#1976d2', background: '#e3f2fd', borderRadius: 4, padding: '0 4px' }}>
                                {precoMercado ? formatPrice(precoMercado) : <span style={{ color: '#f44336', fontWeight: 600 }}>-</span>}
                            </span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                            Anuncio: <span style={{ color: '#388e3c', background: '#e8f5e9', borderRadius: 4, padding: '0 4px' }}>
                                {precoVitrine ? formatPrice(precoVitrine) : <span style={{ color: '#f44336', fontWeight: 600 }}>-</span>}
                            </span>
                        </Typography>
                        {/* Warning chip if both prices are missing/zero */}
                        {(!precoMercado && !precoVitrine) && (
                            <Chip label="Sem preço" size="small" color="warning" sx={{ mt: 0.5, fontWeight: 600, fontSize: '0.75rem' }} />
                        )}
                    </Box>
                </Box>
                {/* Divider below info/prices */}
                <Box sx={{ width: '100%', my: 1 }}>
                    <Box sx={{ borderBottom: '1px solid #eee', width: '100%' }} />
                </Box>
                {/* Edição de preço inline */}
                {isEditing && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 1 }}>
                        <FormControl variant="outlined" size="small">
                            <OutlinedInput
                                value={newPrice}
                                onChange={(e) => setNewPrice(e.target.value)}
                                startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                                autoFocus
                                sx={{ width: 120, mb: 1, height: 32 }}
                            />
                        </FormControl>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Salvar">
                                <IconButton size="small" onClick={handleSavePrice} sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.2)' } }}>
                                    <SaveIcon fontSize="small" sx={{ color: '#4caf50' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                                <IconButton size="small" onClick={handleCancelEditPrice} sx={{ bgcolor: 'rgba(244, 67, 54, 0.1)', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.2)' } }}>
                                    <CloseIcon fontSize="small" sx={{ color: '#f44336' }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default CatalogProductCard;